import gql from 'graphql-tag';
import { type ApolloError, useQuery } from '@apollo/react-hooks';
import { type PanelMember } from '../GSPanelDetail';
import { type PanelFormListData } from '../../../GSAssessment';
import {
  FUNDING_TAGS,
  type FundingPanel,
  type FundingMappings,
} from '../../../../Nomination/components';
import { type SelectOption } from '../../../../../components';
import { checkInvalidDate } from '../../../../Application/graphql';

const GET_GS_PANEL_LIST_DATA = gql`
  query ($filter: PanelFilter) {
    getFundingSourceConfiguration {
      mapping
    }
    programmeChoiceMetadata {
      entryYearList {
        value
        label
      }
    }
    panels(filter: $filter) {
      _id
      applications {
        applicationId
        items {
          column
          content
        }
      }
      panelMembers {
        netId
        jobTitle
        email
        givenName
        surname
        department
        institution
        classification
        isPanelChair
      }
      fundingSource
      entryYear
      panelNo
      title
      panelRound
      panelChair {
        netId
      }
      panelType
      startDate
      endDate
      status
      tagOptions
    }
  }
`;

export const useGetGSPanelList = (
  filter: any,
): {
  gsPanelList: PanelFormListData[];
  fundingsNeedPanel: SelectOption[];
  entryYears: SelectOption[];
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => void;
} => {
  const { data, loading, error, refetch } = useQuery(GET_GS_PANEL_LIST_DATA, {
    variables: { filter },
    fetchPolicy: 'network-only',
  });

  let gsPanelList: PanelFormListData[] = [];

  if (!loading) {
    gsPanelList = data?.panels?.map((items: any): PanelFormListData => {
      const {
        _id,
        fundingSource = '',
        entryYear = '',
        title = '',
        panelType,
        endDate,
        tagOptions = [],
        panelMember: rawPanelMember = [],
        status,
      } = items ?? {};

      const panelChairNetId =
        (rawPanelMember as PanelMember[]).find(member => member.isPanelChair)
          ?.netId ?? '';
      const sortedPanelMembers = rawPanelMember?.sort(
        (a: PanelMember, b: PanelMember) =>
          Number(b.isPanelChair) - Number(a.isPanelChair),
      );

      const panelMember = sortedPanelMembers?.map(
        (member: PanelMember, index: number) => {
          const {
            netId,
            jobTitle,
            email,
            givenName,
            surname,
            department,
            institution,
            classification,
            isPanelChair,
          } = member;

          return {
            order: index,
            netId,
            name: `${surname.toUpperCase()} ${givenName}`,
            jobTitle,
            email,
            givenName,
            surname,
            department,
            institution,
            classification,
            isPanelChair,
          };
        },
      );

      return {
        _id,
        fundingSource,
        entryYear,
        title,
        panelType: (panelType as FundingPanel) ?? '',
        endDate: checkInvalidDate(endDate) ? null : endDate,
        panelChair: panelChairNetId,
        tagOptions,
        panelMember,
        status,
        resultOptions: [], // not used in GSManagement
      };
    });
  }

  const fundingSourceMapping =
    (data?.getFundingSourceConfiguration?.mapping as FundingMappings) ?? {};

  const fundingsNeedPanel: SelectOption[] = Object.values(fundingSourceMapping)
    .flat()
    .filter(funding => funding.tags.includes(FUNDING_TAGS.NEED_PANEL_ROUND))
    .map(item => ({
      value: item.value,
      label: item.label,
    }));
  const entryYears: SelectOption[] =
    data?.programmeChoiceMetadata?.entryYearList;

  return {
    gsPanelList,
    fundingsNeedPanel,
    entryYears,
    loading,
    error,
    refetch,
  };
};
